/**
 * Blok scene: 8x8 board, three pieces to drag, a hidden picture under the board.
 * All rules live in ./core (pure TS); this file only draws, animates and routes input.
 */
import Phaser from "phaser";
import { decodePixels, hashSeed, Rng, type Picture } from "@enjoyment/core";
import { loadPictures, type PicturePack } from "@shared/art/pictures";
import { sfx, unlockAudio } from "@shared/audio";
import { exposeBotHook, loadSave, writeSave } from "@kit/boot";
import { openGuess, paintPicture } from "@kit/guess";
import { currentLang } from "@kit/lang";
import { afterSkip, afterWin, mapBack, mapText, openMap, starLine } from "@kit/map";
import { recordSkip, recordWin, type WinOutcome } from "@kit/progress";
import { track } from "@kit/telemetry";
import { COLORS, FONT, PANEL, TEXT_DIM, W, backdrop, button, centerCamera, confetti, floatText, overlay, share, text, topBar } from "@kit/ui";
import { piece, SIDE, type PieceDef } from "./core/pieces";
import { fits, fullLines, place as placeMask } from "./core/masks";
import {
  CELLS,
  OBSTACLE,
  canPlaceAt,
  createGame,
  guessAnswer,
  masksOf,
  placePiece,
  resultPieces,
  revealedCount,
  revealedFraction,
  shareGrid,
  starsFor,
  streakMultiplier,
  type BlokState,
  type PlaceResult,
} from "./core/game";
import { generateLevel, pickPicture, themePool } from "./core/generator";
import { chooseMove } from "./core/bot";
import { pieceUnit, t, themeName } from "./i18n";

const CELL = 75;
const BOARD = SIDE * CELL; // 600
const OX = (W - BOARD) / 2;
const TEX = 72; // generated cell texture size
const BASE = (CELL - 4) / TEX;
const TRAY_CELL = 44;
const TRAY_S = TRAY_CELL / CELL;
const SLOT_W = 230;
const SLOT_H = 200;
const PREVIEW = 150;
const MAX_FAILS = 3; // H4: after this many losses the level can be skipped for free
const COMBOS = ["combo2", "combo3", "combo4", "combo5", "combo6"] as const;

interface Save {
  level: number;
  /** level -> fewest pieces to a correct guess. */
  best: Record<string, number>;
  /** level -> losses so far (reset on win/skip). */
  fails: Record<string, number>;
  solved: number;
  topScore: number;
}

interface Drag {
  slot: number;
  pointerId: number;
  startX: number;
  startY: number;
  moved: boolean;
  target: { r: number; c: number } | null;
}

export class BlokScene extends Phaser.Scene {
  private levelArg?: number;
  private dead = false;
  private pack?: PicturePack;
  private save!: Save;

  private state!: BlokState;
  private picture!: Picture;
  private pixels: number[] = [];
  private pool: Picture[] = [];
  private attempt = 1;
  private startedAt = 0;
  private oy = 345;
  private trayY = 1085;

  private cellImgs: Phaser.GameObjects.Image[] = [];
  private views: (Phaser.GameObjects.Container | null)[] = [null, null, null];
  private ghost!: Phaser.GameObjects.Graphics;
  private underCtx!: CanvasRenderingContext2D;
  private underTex!: Phaser.Textures.CanvasTexture;
  private prevCtx!: CanvasRenderingContext2D;
  private prevTex!: Phaser.Textures.CanvasTexture;
  private prevImg!: Phaser.GameObjects.Image;
  private hud!: ReturnType<typeof topBar>;
  private themeTxt!: Phaser.GameObjects.Text;
  private revealTxt!: Phaser.GameObjects.Text;
  private scoreTxt!: Phaser.GameObjects.Text;
  private streakTxt!: Phaser.GameObjects.Text;
  private drag: Drag | null = null;
  private selectedSlot: number | null = null;
  private ended = false;
  private auto = false;
  private guessOpen = false;
  private panel?: Phaser.GameObjects.Container;
  private outcome?: WinOutcome;

  constructor() {
    super("blok");
  }

  init(data?: { level?: number }): void {
    this.levelArg = data?.level;
    this.dead = false;
    this.ended = false;
    this.auto = false;
    this.guessOpen = false;
    this.drag = null;
    this.selectedSlot = null;
    this.views = [null, null, null];
    this.cellImgs = [];
    this.panel = undefined;
    this.outcome = undefined;
  }

  create(): void {
    centerCamera(this);
    backdrop(this, { light: 0x8ac926 });
    this.events.once("shutdown", () => {
      this.dead = true;
    });
    this.save = loadSave<Save>("blok", { level: 1, best: {}, fails: {}, solved: 0, topScore: 0 });
    const n = Math.max(1, Math.floor(this.levelArg ?? this.save.level));
    const loading = text(this, W / 2, this.scale.height / 2, t("loading"), 32, TEXT_DIM);
    loadPictures()
      .then((pack) => {
        if (this.dead) return;
        loading.destroy();
        this.pack = pack;
        this.build(n);
      })
      .catch(() => {
        if (this.dead) return;
        loading.setText(t("loadError")).setAlign("center");
        this.input.once("pointerdown", () => this.scene.restart({ level: n }));
      });
  }

  // ------------------------------------------------------------------ build

  private makeTextures(): void {
    if (this.textures.exists("blok-cell")) return;
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 1).fillRoundedRect(0, 0, TEX, TEX, 14);
    g.fillStyle(0x000000, 0.2).fillRoundedRect(4, TEX * 0.58, TEX - 8, TEX * 0.42 - 4, { tl: 0, tr: 0, bl: 12, br: 12 });
    g.fillStyle(0xffffff, 0.35).fillRoundedRect(7, 6, TEX - 14, 9, 4);
    g.generateTexture("blok-cell", TEX, TEX);
    g.clear();
    g.fillStyle(0x4a5080, 1).fillRoundedRect(0, 0, TEX, TEX, 14);
    g.fillStyle(0x363b66, 1).fillRoundedRect(6, 6, TEX - 12, TEX - 12, 9);
    g.lineStyle(7, 0x4a5080, 1).lineBetween(20, 20, TEX - 20, TEX - 20).lineBetween(TEX - 20, 20, 20, TEX - 20);
    g.generateTexture("blok-obst", TEX, TEX);
    g.clear();
    g.fillStyle(0xffffff, 1).fillCircle(7, 7, 7);
    g.generateTexture("blok-spark", 14, 14);
    g.destroy();
  }

  private canvasTex(key: string, size: number): Phaser.Textures.CanvasTexture {
    if (this.textures.exists(key)) this.textures.remove(key);
    const t = this.textures.createCanvas(key, size, size)!;
    t.setFilter(Phaser.Textures.FilterMode.NEAREST);
    return t;
  }

  private cx(c: number): number {
    return OX + c * CELL + CELL / 2;
  }
  private cy(r: number): number {
    return this.oy + r * CELL + CELL / 2;
  }
  private slotX(k: number): number {
    return W / 2 + (k - 1) * (SLOT_W + 5);
  }

  private build(n: number): void {
    this.makeTextures();
    const H = this.scale.height;
    const extra = Math.max(0, H - 1280);
    this.oy = Math.round(345 + extra * 0.3);
    this.trayY = Math.round(this.oy + BOARD + 150 + extra * 0.35);

    const level = generateLevel(n);
    this.picture = pickPicture(level, this.pack!.pictures);
    this.pixels = decodePixels(this.picture);
    this.pool = themePool(level, this.pack!.pictures);
    this.state = createGame(level, this.picture);
    this.attempt = (this.save.fails[String(n)] ?? 0) + 1;
    this.startedAt = Date.now();

    this.hud = topBar(this, t("title", { n }), t("pieces"), mapBack(this, "blok"));

    // --- info row: preview, text, guess button
    this.add.rectangle(W / 2, 243, 680, 168, PANEL, 1).setDepth(0);
    this.prevTex = this.canvasTex("blok-prev", 256);
    this.prevCtx = this.prevTex.context;
    this.add.rectangle(36 + PREVIEW / 2 + 6, 243, PREVIEW + 12, PREVIEW + 12, 0x12152b).setDepth(1);
    this.prevImg = this.add
      .image(42 + PREVIEW / 2, 243, "blok-prev")
      .setDisplaySize(PREVIEW, PREVIEW)
      .setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        this.openGuessCard();
      });
    const tx = 42 + PREVIEW + 28;
    this.themeTxt = this.add.text(tx, 192, t("theme", { theme: themeName(level.theme) }), { fontFamily: FONT, fontSize: "22px", color: "#b8bee6", fontStyle: "bold" }).setOrigin(0, 0.5).setDepth(2);
    this.revealTxt = this.add.text(tx, 228, "", { fontFamily: FONT, fontSize: "30px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0, 0.5).setDepth(2);
    this.scoreTxt = this.add.text(tx, 266, "", { fontFamily: FONT, fontSize: "21px", color: TEXT_DIM }).setOrigin(0, 0.5).setDepth(2);
    this.streakTxt = this.add.text(tx, 294, "", { fontFamily: FONT, fontSize: "21px", color: TEXT_DIM }).setOrigin(0, 0.5).setDepth(2);
    const gb = button(this, 568, 243, 220, 96, t("guess"), 0xff924c, () => this.openGuessCard());
    gb.setDepth(2);

    // --- board
    const bg = this.add.graphics().setDepth(0);
    bg.fillStyle(0x12152b, 1).fillRoundedRect(OX - 12, this.oy - 12, BOARD + 24, BOARD + 24, 22);
    this.underTex = this.canvasTex("blok-under", 32);
    this.underCtx = this.underTex.context;
    this.add.image(OX, this.oy, "blok-under").setOrigin(0).setDisplaySize(BOARD, BOARD).setAlpha(0.5).setDepth(1);
    const grid = this.add.graphics().setDepth(2);
    grid.fillStyle(0x0d1024, 0.58);
    for (let i = 0; i < CELLS; i++) {
      const r = Math.floor(i / SIDE);
      const c = i % SIDE;
      grid.fillRoundedRect(OX + c * CELL + 3, this.oy + r * CELL + 3, CELL - 6, CELL - 6, 11);
    }
    for (let i = 0; i < CELLS; i++) {
      const img = this.add
        .image(this.cx(i % SIDE), this.cy(Math.floor(i / SIDE)), "blok-cell")
        .setScale(BASE)
        .setDepth(3)
        .setVisible(false);
      this.cellImgs.push(img);
    }
    this.ghost = this.add.graphics().setDepth(4);
    // Empty and occupied board cells accept a tap after a tray piece is selected.
    for (let i = 0; i < CELLS; i++) {
      const r = Math.floor(i / SIDE);
      const c = i % SIDE;
      this.add
        .zone(this.cx(c), this.cy(r), CELL, CELL)
        .setInteractive()
        .setDepth(5)
        .on("pointerdown", () => this.placeSelected(r, c));
    }

    // --- trays
    const tg = this.add.graphics().setDepth(0);
    for (let k = 0; k < 3; k++) tg.fillStyle(PANEL, 0.7).fillRoundedRect(this.slotX(k) - SLOT_W / 2, this.trayY - SLOT_H / 2, SLOT_W, SLOT_H, 22);
    for (let k = 0; k < 3; k++) {
      this.add
        .zone(this.slotX(k), this.trayY, SLOT_W, SLOT_H)
        .setInteractive()
        .setDepth(5)
        .on("pointerdown", (p: Phaser.Input.Pointer) => this.startDrag(k, p));
    }
    this.input.on("pointermove", (p: Phaser.Input.Pointer) => this.moveDrag(p));
    this.input.on("pointerup", (p: Phaser.Input.Pointer) => this.endDrag(p));
    this.input.on("pointerupoutside", (p: Phaser.Input.Pointer) => this.endDrag(p));

    if ((this.save.fails[String(n)] ?? 0) >= MAX_FAILS) {
      text(this, W / 2, H - 52, t("skipLevel"), 26, "#ffca3a")
        .setDepth(5)
        .setInteractive({ useHandCursor: true })
        .on("pointerdown", () => this.skipLevel());
    }

    this.redrawPicture();
    this.renderBoard();
    this.dealViews(true);
    this.updateHud();

    track("level_start", { level: n, attempt: this.attempt, theme: level.theme });
    exposeBotHook({
      state: () => this.botState(),
      autoplay: () => this.autoplay(),
      blunder: () => this.blunder(),
      solve: () => this.submitGuess(this.picture.label.tr ?? this.picture.id),
      picture: () => this.picture.id,
    });
    if (this.state.status === "over") this.time.delayedCall(400, () => this.gameOver());
  }

  // ------------------------------------------------------------------ drawing helpers

  private tint(v: number): number {
    return COLORS[(v - 1) % COLORS.length]!;
  }

  private renderBoard(pop?: Set<number>): void {
    for (let i = 0; i < CELLS; i++) {
      const v = this.state.board[i]!;
      const img = this.cellImgs[i]!;
      if (!v) {
        img.setVisible(false);
        continue;
      }
      img.setVisible(true).setTexture(v === OBSTACLE ? "blok-obst" : "blok-cell");
      if (v === OBSTACLE) img.clearTint();
      else img.setTint(this.tint(v));
      if (pop?.has(i)) {
        img.setScale(BASE * 1.3);
        this.tweens.add({ targets: img, scale: BASE, duration: 160, ease: "Back.Out" });
      } else img.setScale(BASE);
    }
  }

  /** Picture layers: faint underlay under the board + full side preview. */
  private redrawPicture(): void {
    const ctx = this.underCtx;
    ctx.clearRect(0, 0, 32, 32);
    const size = this.picture.size;
    const bs = size / SIDE;
    for (let i = 0; i < CELLS; i++) {
      if (!this.state.revealed[i]) continue;
      const r = Math.floor(i / SIDE);
      const c = i % SIDE;
      for (let y = r * bs; y < (r + 1) * bs; y++)
        for (let x = c * bs; x < (c + 1) * bs; x++) {
          const col = this.pixels[y * size + x]!;
          if (col < 0) continue;
          ctx.fillStyle = this.picture.palette[col]!;
          ctx.fillRect(x, y, 1, 1);
        }
    }
    this.underTex.refresh();
    paintPicture(this.prevCtx, 256, this.picture, this.pixels, this.state.revealed, SIDE, SIDE);
    this.prevTex.refresh();
  }

  private updateHud(): void {
    const s = this.state;
    this.hud.value.setText(String(s.placed));
    this.revealTxt.setText(t("revealed", { n: revealedCount(s), total: CELLS }));
    const firstMoveHint = s.level.n === 1 && s.placed === 0;
    this.scoreTxt.setText(this.selectedSlot !== null
      ? t("selected")
      : firstMoveHint
        ? t("firstMove")
        : t("score", { score: s.score, lines: s.lines }));
    const m = streakMultiplier(s.streak);
    this.streakTxt.setText(firstMoveHint ? t("firstHint") : s.streak >= 2 ? t("streak", { m: m % 1 ? m.toFixed(1) : m }) : "").setColor(firstMoveHint ? "#b8bee6" : s.streak >= 2 ? "#ffca3a" : TEXT_DIM);
  }

  // ------------------------------------------------------------------ trays + drag

  private makePieceView(id: string): Phaser.GameObjects.Container {
    const p = piece(id);
    const c = this.add.container(0, 0).setDepth(6);
    for (const [r, col] of p.cells) {
      const img = this.add
        .image((col - (p.w - 1) / 2) * CELL, (r - (p.h - 1) / 2) * CELL, "blok-cell")
        .setScale(BASE)
        .setTint(COLORS[p.color % COLORS.length]!);
      c.add(img);
    }
    return c;
  }

  private dealViews(slide: boolean): void {
    for (let k = 0; k < 3; k++) {
      this.views[k]?.destroy();
      this.views[k] = null;
      const id = this.state.tray[k];
      if (!id) continue;
      const v = this.makePieceView(id).setPosition(this.slotX(k), this.trayY).setScale(TRAY_S);
      this.views[k] = v;
      if (slide) {
        v.setAlpha(0).y += 50;
        this.tweens.add({ targets: v, alpha: 1, y: this.trayY, duration: 260, delay: k * 70, ease: "Back.Out" });
      }
    }
  }

  private lift(p: PieceDef): number {
    return (p.h * CELL) / 2 + 70;
  }

  /** Board position the dragged piece would snap to (nearest free spot within ~1 cell), or null. */
  private snapTarget(view: Phaser.GameObjects.Container, p: PieceDef): { r: number; c: number } | null {
    const fc = (view.x - (p.w * CELL) / 2 - OX) / CELL;
    const fr = (view.y - (p.h * CELL) / 2 - this.oy) / CELL;
    const m = masksOf(this.state);
    let best: { r: number; c: number } | null = null;
    let bestD = 0.95;
    for (let dr = -1; dr <= 1; dr++)
      for (let dc = -1; dc <= 1; dc++) {
        const r = Math.round(fr) + dr;
        const c = Math.round(fc) + dc;
        const d = Math.hypot(r - fr, c - fc);
        if (d < bestD && fits(m, p, r, c)) {
          bestD = d;
          best = { r, c };
        }
      }
    return best;
  }

  private startDrag(slot: number, p: Phaser.Input.Pointer): void {
    unlockAudio();
    const id = this.state.tray[slot];
    const view = this.views[slot];
    if (this.drag || this.state.status !== "playing" || this.ended || !id || !view) return;
    this.clearSelection();
    this.drag = { slot, pointerId: p.id, startX: p.worldX, startY: p.worldY, moved: false, target: null };
    view.setDepth(100);
    this.tweens.killTweensOf(view);
    this.tweens.add({ targets: view, scale: 1, alpha: 1, duration: 90, ease: "Quad.Out" });
    this.moveDrag(p);
  }

  private moveDrag(p: Phaser.Input.Pointer): void {
    const d = this.drag;
    if (!d || p.id !== d.pointerId) return;
    const view = this.views[d.slot];
    const id = this.state.tray[d.slot];
    if (!view || !id) return;
    const pd = piece(id);
    if (Math.hypot(p.worldX - d.startX, p.worldY - d.startY) > 10) d.moved = true;
    view.setPosition(p.worldX, p.worldY - this.lift(pd));
    d.target = this.snapTarget(view, pd);
    this.drawGhost(pd, d.target);
  }

  private drawGhost(pd: PieceDef, t: { r: number; c: number } | null): void {
    const g = this.ghost;
    g.clear();
    if (!t) return;
    const m = masksOf(this.state);
    placeMask(m, pd, t.r, t.c);
    const { rows, cols } = fullLines(m);
    // Lines this drop would complete: pale wash + outline.
    g.fillStyle(0xffffff, 0.16);
    g.lineStyle(3, 0xffffff, 0.55);
    for (const r of rows) {
      g.fillRect(OX, this.oy + r * CELL, BOARD, CELL);
      g.strokeRect(OX + 1, this.oy + r * CELL + 1, BOARD - 2, CELL - 2);
    }
    for (const c of cols) {
      g.fillRect(OX + c * CELL, this.oy, CELL, BOARD);
      g.strokeRect(OX + c * CELL + 1, this.oy + 1, CELL - 2, BOARD - 2);
    }
    g.fillStyle(COLORS[pd.color % COLORS.length]!, 0.5);
    for (const [r, c] of pd.cells) g.fillRoundedRect(OX + (t.c + c) * CELL + 4, this.oy + (t.r + r) * CELL + 4, CELL - 8, CELL - 8, 12);
  }

  private endDrag(p: Phaser.Input.Pointer): void {
    const d = this.drag;
    if (!d || p.id !== d.pointerId) return;
    this.drag = null;
    this.ghost.clear();
    const view = this.views[d.slot];
    if (!view) return;
    if (d.target && this.state.status === "playing") {
      this.commit(d.slot, d.target.r, d.target.c);
      return;
    }
    if (!d.moved && this.state.status === "playing") {
      this.selectSlot(d.slot);
      view.setDepth(6);
      view.setPosition(this.slotX(d.slot), this.trayY).setScale(TRAY_S * 1.08);
      return;
    }
    if (d.moved) sfx.nope();
    view.setDepth(6);
    this.tweens.add({ targets: view, x: this.slotX(d.slot), y: this.trayY, scale: TRAY_S, duration: 170, ease: "Back.Out" });
  }

  private selectSlot(slot: number): void {
    if (this.state.status !== "playing" || this.ended || !this.state.tray[slot]) return;
    this.selectedSlot = slot;
    for (let i = 0; i < this.views.length; i++) {
      const view = this.views[i];
      if (view) view.setScale(TRAY_S * (i === slot ? 1.08 : 1));
    }
    this.ghost.clear();
    const id = this.state.tray[slot]!;
    const pd = piece(id);
    this.ghost.fillStyle(0x8ac926, 0.95);
    for (let r = 0; r + pd.h <= SIDE; r++) {
      for (let c = 0; c + pd.w <= SIDE; c++) {
        if (canPlaceAt(this.state, slot, r, c)) {
          // The dot marks the top-left anchor cell to tap; green is paired with a text instruction.
          this.ghost.fillCircle(OX + c * CELL + 13, this.oy + r * CELL + 13, 7);
        }
      }
    }
    this.updateHud();
  }

  private clearSelection(): void {
    this.selectedSlot = null;
    this.ghost.clear();
    for (const view of this.views) view?.setScale(TRAY_S);
    if (this.state) this.updateHud();
  }

  private placeSelected(r: number, c: number): void {
    const slot = this.selectedSlot;
    if (slot === null || this.drag || this.ended || this.state.status !== "playing") return;
    if (!canPlaceAt(this.state, slot, r, c)) {
      sfx.nope();
      return;
    }
    this.commit(slot, r, c);
  }

  // ------------------------------------------------------------------ placement + effects

  /** Apply a legal placement and play every effect. Shared by drag, bot autoplay and tests of the scene. */
  private commit(slot: number, r: number, c: number): void {
    const pre = this.state.board.slice();
    const wasPlaying = this.state.status === "playing";
    const res = placePiece(this.state, slot, r, c);
    if (!res || !wasPlaying) return;
    this.selectedSlot = null;
    this.ghost.clear();
    const color = res.piece.color + 1;
    for (const i of res.cells) pre[i] = color;
    this.views[slot]?.destroy();
    this.views[slot] = null;

    const popped = new Set(res.cells.filter((i) => !res.cleared.includes(i)));
    this.renderBoard(popped);
    if (res.lines > 0) this.playClear(res, pre, r, c);
    else sfx.goalTick();
    this.updateHud();
    track("move", { level: this.state.level.n, piece: res.piece.id, lines: res.lines, placed: this.state.placed });

    if (res.dealt) this.time.delayedCall(res.lines > 0 ? 380 : 120, () => !this.dead && this.dealViews(true));
    if (res.status === "over") this.time.delayedCall(res.lines > 0 ? 1100 : 700, () => this.gameOver());
  }

  private playClear(res: PlaceResult, pre: number[], r0: number, c0: number): void {
    const s = this.state;
    const centerR = r0 + res.piece.h / 2;
    const centerC = c0 + res.piece.w / 2;

    // Sweep flashes along each cleared line.
    const sweep = (x: number, y: number, w: number, h: number, horizontal: boolean) => {
      const bar = this.add.rectangle(x, y, w, h, 0xffffff, 0.85).setOrigin(horizontal ? 0 : 0.5, horizontal ? 0.5 : 0).setDepth(20);
      if (horizontal) bar.setScale(0, 1);
      else bar.setScale(1, 0);
      this.tweens.add({ targets: bar, scaleX: 1, scaleY: 1, duration: 150, ease: "Quad.Out" });
      this.tweens.add({ targets: bar, alpha: 0, duration: 260, delay: 140, onComplete: () => bar.destroy() });
    };
    for (const r of res.rows) sweep(OX, this.cy(r), BOARD, CELL - 6, true);
    for (const c of res.cols) sweep(this.cx(c), this.oy, CELL - 6, BOARD, false);

    // Cleared blocks shrink away, nearest to the dropped piece first; sparks fly from each.
    for (const i of res.cleared) {
      const r = Math.floor(i / SIDE);
      const c = i % SIDE;
      const v = pre[i]!;
      const delay = Math.hypot(r + 0.5 - centerR, c + 0.5 - centerC) * 28;
      const ghost = this.add
        .image(this.cx(c), this.cy(r), v === OBSTACLE ? "blok-obst" : "blok-cell")
        .setScale(BASE)
        .setDepth(15);
      if (v !== OBSTACLE) ghost.setTint(this.tint(v));
      this.tweens.add({
        targets: ghost,
        scale: BASE * 1.25,
        alpha: 0,
        duration: 260,
        delay: delay + 70,
        ease: "Quad.In",
        onStart: () => ghost.setTintFill(0xffffff),
        onComplete: () => ghost.destroy(),
      });
      this.time.delayedCall(delay + 70, () => !this.dead && this.sparks(this.cx(c), this.cy(r), v === OBSTACLE ? 0x8f96c2 : this.tint(v), 3));
    }

    // Reveal: flash the new picture blocks, then redraw the picture layers.
    this.time.delayedCall(260, () => {
      if (this.dead) return;
      this.redrawPicture();
      this.updateHud();
      for (const i of res.newlyRevealed) {
        const f = this.add.rectangle(this.cx(i % SIDE), this.cy(Math.floor(i / SIDE)), CELL - 6, CELL - 6, 0xffffff, 0.75).setDepth(16);
        this.tweens.add({ targets: f, alpha: 0, scale: 1.12, duration: 520, onComplete: () => f.destroy() });
      }
      if (res.newlyRevealed.length) {
        const base = PREVIEW / 256; // absolute, so overlapping pulses cannot compound
        this.tweens.killTweensOf(this.prevImg);
        this.prevImg.setScale(base);
        this.tweens.add({ targets: this.prevImg, scale: base * 1.1, duration: 140, yoyo: true, onComplete: () => this.prevImg.setScale(base) });
      }
    });

    // Sound, shake, combo text.
    sfx.pop(Math.min(10, 2 + res.lines * 2 + s.streak - 1));
    if (res.lines >= 3) {
      sfx.special();
      this.cameras.main.shake(240, 0.008 + Math.min(0.01, res.lines * 0.002));
      track("moment", { type: "big_combo", lines: res.lines, level: s.level.n });
    }
    const mid = this.oy + BOARD / 2;
    floatText(this, W / 2, mid - (res.lines >= 2 ? 30 : 0), `+${res.points}`, "#ffffff", res.lines >= 2 ? 52 : 40);
    if (res.lines >= 2) floatText(this, W / 2, mid - 100, t(COMBOS[Math.min(6, res.lines) - 2]!), "#ffca3a", 64);
    if (res.streak >= 2) floatText(this, W / 2, mid + 50, t("streak", { m: streakMultiplier(res.streak) }), "#ff924c", 38);
    if (res.perfect) {
      floatText(this, W / 2, mid - 170, t("perfect"), "#8ac926", 56);
      confetti(this);
    }
  }

  private sparks(x: number, y: number, color: number, n: number): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const d = 30 + Math.random() * 50;
      const sp = this.add.image(x, y, "blok-spark").setTint(color).setDepth(18).setScale(0.5 + Math.random() * 0.5);
      this.tweens.add({
        targets: sp,
        x: x + Math.cos(a) * d,
        y: y + Math.sin(a) * d + 24,
        alpha: 0,
        scale: 0,
        duration: 380 + Math.random() * 280,
        ease: "Quad.Out",
        onComplete: () => sp.destroy(),
      });
    }
  }

  // ------------------------------------------------------------------ guess / win / lose

  private openGuessCard(): void {
    if (this.guessOpen || this.ended || this.state.status !== "playing") return;
    unlockAudio();
    this.guessOpen = true;
    track("guess_open", { level: this.state.level.n, placed: this.state.placed, revealed: revealedCount(this.state) });
    openGuess(this, {
      question: t("question", { theme: themeName(this.state.level.theme, "") }),
      pool: this.pool,
      lang: currentLang(),
      hint: t("guessHint"),
      paint: (ctx, size) => paintPicture(ctx, size, this.picture, this.pixels, this.state.revealed, SIDE, SIDE),
      tried: () => this.state.wrongLabels,
      onSubmit: (answer) => this.submitGuess(answer),
      onClose: () => {
        this.guessOpen = false;
        if (this.state.status === "won" && !this.ended) this.win();
      },
    });
  }

  private submitGuess(answer: string): { correct: boolean; message: string; finished: boolean } {
    const s = this.state;
    const out = guessAnswer(s, answer);
    track("guess", { level: s.level.n, correct: out.correct, placed: s.placed, wrong: s.wrong });
    this.updateHud();
    if (out.correct) {
      if (!this.guessOpen) this.time.delayedCall(0, () => !this.ended && this.win()); // programmatic solve (bot hook)
      return { correct: true, message: t("correct", { pieces: s.solvedAt, unit: pieceUnit(s.solvedAt) }), finished: true };
    }
    if (out.repeat) return { correct: false, message: t("repeat"), finished: false };
    sfx.nope();
    return { correct: false, message: t("wrong", { penalty: out.penalty }), finished: false };
  }

  private win(): void {
    if (this.ended) return;
    this.ended = true;
    const s = this.state;
    const n = s.level.n;
    const secs = Math.round((Date.now() - this.startedAt) / 1000);
    track("level_end", { level: n, result: "won", moves: s.solvedAt, secs, wrong: s.wrong, attempt: this.attempt });
    if (s.solvedAt <= 8) track("moment", { type: "quick_solve", level: n, pieces: s.solvedAt });
    const best = this.save.best[String(n)];
    const pieces = resultPieces(s);
    if (best === undefined || pieces < best) this.save.best[String(n)] = pieces;
    delete this.save.fails[String(n)];
    this.save.solved++;
    this.save.level = Math.max(this.save.level, n + 1);
    this.save.topScore = Math.max(this.save.topScore, s.score);
    writeSave("blok", this.save);
    this.outcome = recordWin("blok", n, starsFor(s));
    sfx.win();
    confetti(this);
    this.showWin();
  }

  private showWin(): void {
    const s = this.state;
    const H = this.scale.height;
    const cy = H / 2;
    const c = this.add.container(0, 0).setDepth(1000);
    const dim = this.add.rectangle(W / 2, H / 2, W * 3, H, 0x000000, 0.65).setInteractive();
    const panel = this.add.graphics();
    panel.fillStyle(PANEL, 1).fillRoundedRect(W / 2 - 320, cy - 400, 640, 800, 26);
    panel.lineStyle(4, 0x3a4170, 1).strokeRoundedRect(W / 2 - 320, cy - 400, 640, 800, 26);
    c.add([dim, panel, text(this, W / 2, cy - 340, t("won"), 62, "#8ac926")]);

    // Full picture (left) next to the "where you looked" grid (right).
    const pic = document.createElement("canvas");
    pic.width = pic.height = 256;
    paintPicture(pic.getContext("2d")!, 256, this.picture, this.pixels, new Array<boolean>(CELLS).fill(true), SIDE, SIDE);
    const key = "blok-win-pic";
    if (this.textures.exists(key)) this.textures.remove(key);
    this.textures.addCanvas(key, pic)!.setFilter(Phaser.Textures.FilterMode.NEAREST);
    const S = 250;
    const left = W / 2 - 150;
    const right = W / 2 + 150;
    c.add(this.add.image(left, cy - 170, key).setDisplaySize(S, S));
    const g = this.add.graphics();
    const cs = S / SIDE;
    for (let i = 0; i < CELLS; i++) {
      g.fillStyle(s.revealed[i] ? 0x8ac926 : 0x30365f, 1);
      g.fillRoundedRect(right - S / 2 + (i % SIDE) * cs + 1.5, cy - 170 - S / 2 + Math.floor(i / SIDE) * cs + 1.5, cs - 3, cs - 3, 5);
    }
    c.add(g);
    c.add(text(this, left, cy - 20, this.picture.label[currentLang()] ?? this.picture.label.tr ?? this.picture.id, 30));
    c.add(text(this, right, cy - 20, t("wonOpen", { n: revealedCount(s), total: CELLS }), 26, "#b8bee6", false));
    c.add(text(this, W / 2, cy + 60, t("wonSub", { pieces: s.solvedAt, unit: pieceUnit(s.solvedAt) }), 50));
    const extra = [s.wrong ? t("wrongCount", { wrong: s.wrong }) : t("noWrong"), t("points", { score: s.score }), t("bestStreak", { m: streakMultiplier(s.bestStreak) })].join("  ·  ");
    c.add(text(this, W / 2, cy + 120, extra, 23, "#b8bee6", false));
    const best = this.save.best[String(s.level.n)];
    if (best !== undefined) c.add(text(this, W / 2, cy + 156, resultPieces(s) <= best ? t("newBest") : t("best", { best }), 23, "#ffca3a"));
    c.add(text(this, W / 2, cy + 202, starLine(starsFor(s)), 42, "#ffd23f"));
    const url = `${location.origin}${location.pathname}?ref=share`;
    c.add(
      button(this, W / 2 - 150, cy + 290, 270, 96, t("share"), 0x1982c4, () => {
        track("share_click", { level: s.level.n, pieces: s.solvedAt });
        void share(this.shareBody(), url, t("name")).then((ok) => {
          if (ok) {
            track("share_done", { level: s.level.n });
            floatText(this, W / 2 - 150, cy + 220, t("shared"), "#8ac926", 34);
          }
        });
      }),
    );
    const outcome = this.outcome;
    c.add(button(this, W / 2 + 150, cy + 290, 270, 96, mapText("continue"), 0x8ac926, () => (outcome ? openMap(this, "blok", afterWin(s.level.n, outcome)) : this.scene.restart({ level: s.level.n + 1 })), "#12152b"));
    c.setAlpha(0);
    this.tweens.add({ targets: c, alpha: 1, duration: 220 });
    this.panel = c;
  }

  private gameOver(): void {
    if (this.dead || this.ended || this.state.status !== "over") return;
    this.ended = true;
    const s = this.state;
    const n = String(s.level.n);
    const fails = (this.save.fails[n] ?? 0) + 1;
    this.save.fails[n] = fails;
    this.save.topScore = Math.max(this.save.topScore, s.score);
    writeSave("blok", this.save);
    track("level_end", { level: s.level.n, result: "lost", moves: s.placed, secs: Math.round((Date.now() - this.startedAt) / 1000), fails });
    sfx.lose();
    const can = fails >= MAX_FAILS;
    const sub = `${t("noFit")}\n${t("lostStats", { placed: s.placed, unit: pieceUnit(s.placed), open: revealedCount(s), total: CELLS })}${can ? `\n${t("skipHelp")}` : ""}`;
    this.panel = overlay(this, t("lost"), sub, [
      { label: t("retry"), color: 0x1982c4, onClick: () => this.scene.restart({ level: s.level.n }) },
      ...(can ? [{ label: t("skip"), color: 0x6a4c93, onClick: () => this.skipLevel() }] : []),
    ]);
  }

  /** Share line + Wordle-style grid. A retry (attempt > 1) says so: it already knew the picture. */
  private shareBody(): string {
    const s = this.state;
    const wrong = s.wrong ? t("shareWrong", { wrong: s.wrong }) : "";
    const again = this.attempt > 1 ? t("shareAgain", { attempt: this.attempt }) : "";
    return `${t("shareText", { pieces: s.solvedAt, unit: pieceUnit(s.solvedAt), wrong, n: s.level.n, again })}\n${shareGrid(s)}`;
  }

  /** H4: a level that has beaten the player MAX_FAILS times can always be skipped, for free. */
  private skipLevel(): void {
    const s = this.state;
    const n = s.level.n;
    if (this.save.level <= n) this.save.level = n + 1;
    delete this.save.fails[String(n)];
    writeSave("blok", this.save);
    const outcome = recordSkip("blok", n);
    if (!this.ended) track("level_end", { level: n, result: "skip", moves: s.placed, secs: Math.round((Date.now() - this.startedAt) / 1000) });
    else if (s.status === "over") track("level_skip", { level: n });
    openMap(this, "blok", afterSkip(n, outcome));
  }

  // ------------------------------------------------------------------ bot hook

  private botState(): Record<string, unknown> {
    const s = this.state;
    return {
      level: s.level.n,
      status: s.status,
      placed: s.placed,
      score: s.score,
      streak: s.streak,
      revealed: revealedCount(s),
      fraction: revealedFraction(s),
      tray: s.tray.slice(),
      solvedAt: s.solvedAt,
      wrong: s.wrong,
      attempt: this.attempt,
    };
  }

  /** Bot hook: one random legal move (seeded), for video/smoke runs that want to reach game over. */
  private blunder(): void {
    const s = this.state;
    if (s.status !== "playing" || this.ended) return;
    const legal: { slot: number; r: number; c: number }[] = [];
    s.tray.forEach((id, slot) => {
      if (!id) return;
      const p = piece(id);
      for (let r = 0; r + p.h <= SIDE; r++) for (let c = 0; c + p.w <= SIDE; c++) if (canPlaceAt(s, slot, r, c)) legal.push({ slot, r, c });
    });
    const mv = legal[new Rng(hashSeed(`blunder:${s.level.seed}:${s.placed}`)).int(legal.length)];
    if (mv) this.commit(mv.slot, mv.r, mv.c);
  }

  /** Plays the live scene with the greedy bot until the picture is ~half open (or the board is dead). */
  private autoplay(): void {
    if (this.auto || !this.state) return;
    this.auto = true;
    const step = () => {
      const s = this.state;
      if (this.dead || s.status !== "playing" || this.ended || revealedFraction(s) >= 0.5) {
        this.auto = false;
        return;
      }
      const mv = chooseMove(s);
      if (!mv) {
        this.auto = false;
        return;
      }
      this.commit(mv.slot, mv.r, mv.c);
      window.setTimeout(step, 350); // wall-clock pacing: smoke runs on slow software GL, where game time crawls
    };
    step();
  }
}
